import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';

import { WS_EVENTS } from '../../../../lib/websocket/events';
import { useWebSocket } from '../../../../lib/websocket/WebSocketProvider';

import { mcpConnectorDetailKey, MCP_CONNECTORS_LIST_KEY } from './use-mcp-connectors';

import type { WsEventEnvelope } from '../../../../lib/websocket/events';

export interface McpHandshakeCompletedPayload {
  connectorId: string;
  outcome: 'ok' | 'unreachable' | 'invalid-response' | 'business-error' | 'version-mismatch';
  contractVersion?: string;
  discoveredTools?: string[];
  errorCode?: string;
}

export interface IntegrationsFeatureToggledPayload {
  paused: boolean;
  pausedAt: string | null;
  affectedConnectorCount: number;
}

/**
 * Sprint A4 S1 PR 3 — Tasks 10+16 (refinado pós-merge para escopo restrito).
 *
 * Subscribe canal WS do tenant em 2 eventos — escopo `/admin/integrations`:
 *   - `mcp.handshake-completed`: invalida list + detail (card transita
 *     visualmente de pending-handshake → active/failed sem refresh manual).
 *   - `integrations.feature-toggled`: invalida list (cards refletem
 *     paused/active sem refresh manual).
 *
 * **A invalidação cross-feature de `customer-context`** (regra do silêncio em
 * ContextBlock quando admin pausa em background) vive no shell — hook
 * `useIntegrationsFeatureToggleInvalidate` montado em AppShell. Garante que
 * a invalidação ocorre mesmo quando atendente está em uma conversa (não na
 * página de admin).
 *
 * Pattern espelho do useCustomerContext (Sub-fatia 10.0 PR 3) + listeners
 * WS canonical universais via `useWebSocket().subscribe`.
 */
export function useMcpHandshakeListener(): void {
  const qc = useQueryClient();
  const { subscribe } = useWebSocket();

  useEffect(() => {
    const offHandshake = subscribe<McpHandshakeCompletedPayload>(
      WS_EVENTS.MCP_HANDSHAKE_COMPLETED,
      (envelope: WsEventEnvelope<McpHandshakeCompletedPayload>) => {
        void qc.invalidateQueries({ queryKey: MCP_CONNECTORS_LIST_KEY });
        void qc.invalidateQueries({
          queryKey: mcpConnectorDetailKey(envelope.data.connectorId),
        });
      },
    );

    const offToggled = subscribe<IntegrationsFeatureToggledPayload>(
      WS_EVENTS.INTEGRATIONS_FEATURE_TOGGLED,
      () => {
        void qc.invalidateQueries({ queryKey: MCP_CONNECTORS_LIST_KEY });
      },
    );

    return () => {
      offHandshake();
      offToggled();
    };
  }, [qc, subscribe]);
}
